import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatDateTime } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';

interface Richtlinie {
  id: string;
  schluessel: string;
  bezeichnung: string;
  beschreibung: string;
  rechtsgrundlage: string;
  frist_monate: number;
  wirkung: 'loeschen' | 'anonymisieren';
  aktiv: boolean;
  geaendert_am: string;
}

interface Sperre {
  id: string;
  grund: string;
  gilt_ab: string;
  gilt_bis: string | null;
  angelegt_am: string;
  angelegt_von_name: string | null;
  laeuft: boolean;
  beendbar: boolean;
}

interface Befund {
  schluessel: string;
  bezeichnung: string;
  rechtsgrundlage: string;
  fristMonate: number;
  wirkung: 'loeschen' | 'anonymisieren';
  aktiv: boolean;
  faellig: number;
  aeltester: string | null;
  fehler: string | null;
}

interface Bericht {
  gelaufenAm: string;
  gesperrt: boolean;
  sperren: Sperre[];
  befunde: Befund[];
  faelligGesamt: number;
}

interface Lauf {
  id: string;
  gelaufen_am: string;
  gesperrt: boolean;
  ergebnis: Befund[];
  ausgeloest_von_name: string | null;
}

/** Monate lesbar machen: 96 sind acht Jahre, nicht sechsundneunzig Monate. */
function fristText(monate: number): string {
  if (monate === 0) return 'sofort';
  if (monate % 12 === 0) return `${monate / 12} Jahre`;
  return `${monate} Monate`;
}

export function RetentionPage() {
  const [richtlinien, setRichtlinien] = useState<Richtlinie[] | null>(null);
  const [sperren, setSperren] = useState<Sperre[]>([]);
  const [laeufe, setLaeufe] = useState<Lauf[]>([]);
  const [bericht, setBericht] = useState<Bericht | null>(null);

  const [fristen, setFristen] = useState<Record<string, string>>({});
  const [neueSperre, setNeueSperre] = useState<{ grund: string; giltBis: string } | null>(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [regeln, holds, runs] = await Promise.all([
        api<Richtlinie[]>('/api/retention/policies'),
        api<Sperre[]>('/api/retention/holds'),
        api<Lauf[]>('/api/retention/runs'),
      ]);
      setRichtlinien(regeln);
      setSperren(holds);
      setLaeufe(runs);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function laufen() {
    setBusy(true);
    setError('');
    try {
      setBericht(await api<Bericht>('/api/retention/report', { method: 'POST' }));
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function fristSpeichern(regel: Richtlinie) {
    const wert = fristen[regel.id];
    if (wert === undefined) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/retention/policies/${regel.id}`, {
        method: 'PUT',
        body: { fristMonate: Number(wert) },
      });
      setMessage(`Frist für "${regel.bezeichnung}" gespeichert.`);
      setFristen({ ...fristen, [regel.id]: undefined as unknown as string });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function umschalten(regel: Richtlinie) {
    setBusy(true);
    try {
      await api(`/api/retention/policies/${regel.id}`, {
        method: 'PUT',
        body: { aktiv: !regel.aktiv },
      });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function sperreAnlegen() {
    if (!neueSperre) return;
    setBusy(true);
    setError('');
    try {
      await api('/api/retention/holds', {
        method: 'POST',
        body: { grund: neueSperre.grund, giltBis: neueSperre.giltBis || null },
      });
      setMessage('Aufbewahrungssperre gesetzt. Bis auf Weiteres wird nichts gelöscht.');
      setNeueSperre(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function sperreBeenden(sperre: Sperre) {
    setBusy(true);
    try {
      await api(`/api/retention/holds/${sperre.id}/end`, { method: 'POST' });
      setMessage('Sperre zum heutigen Tag beendet.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!richtlinien) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  const laufendeSperre = sperren.find((sperre) => sperre.laeuft);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Notice kind="info">
        <strong>Es wird nichts gelöscht.</strong> Der Lauf berichtet, was eine Regel greifen würde.
        Scharf geschaltet wird erst, wenn die Fristen steuerlich bestätigt sind und die Berichte über
        mehrere Monate plausibel waren.
      </Notice>

      {laufendeSperre && (
        <Notice kind="info">
          Aufbewahrungssperre aktiv seit {formatDate(laufendeSperre.gilt_ab)}:{' '}
          {laufendeSperre.grund}. Solange sie gilt, wird auch nach dem Scharfschalten nichts
          gelöscht.
        </Notice>
      )}

      <Card
        title="Aufbewahrungsregeln"
        actions={
          <button className="primary small" onClick={laufen} disabled={busy}>
            {busy ? 'Läuft …' : 'Trockenlauf starten'}
          </button>
        }
        padded={false}
      >
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Die Frist beginnt jeweils mit <strong>Ablauf des Kalenderjahres</strong>, in dem der Satz
          entstanden ist — nicht am Belegdatum. Die Vorgaben sind ein Vorschlag und gehören vom
          Steuerberater bestätigt.
        </div>
        <table>
          <thead>
            <tr>
              <th>Datenklasse</th>
              <th>Rechtsgrundlage</th>
              <th>Frist</th>
              <th>Danach</th>
              <th className="num">Fällig</th>
              <th>Ältester</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {richtlinien.map((regel) => {
              const befund = bericht?.befunde.find((eintrag) => eintrag.schluessel === regel.schluessel);
              return (
                <tr key={regel.id} style={regel.aktiv ? undefined : { opacity: 0.5 }}>
                  <td>
                    <div>{regel.bezeichnung}</div>
                    <div className="small muted">{regel.beschreibung}</div>
                  </td>
                  <td className="small muted">{regel.rechtsgrundlage}</td>
                  <td style={{ width: 150 }}>
                    <input
                      style={{ width: 70, textAlign: 'right' }}
                      value={fristen[regel.id] ?? String(regel.frist_monate)}
                      onChange={(e) => setFristen({ ...fristen, [regel.id]: e.target.value })}
                      onBlur={() => fristSpeichern(regel)}
                    />{' '}
                    <span className="small muted">Mon. ({fristText(regel.frist_monate)})</span>
                  </td>
                  <td>
                    <Badge kind={regel.wirkung === 'loeschen' ? 'bad' : 'warn'}>
                      {regel.wirkung === 'loeschen' ? 'löschen' : 'anonymisieren'}
                    </Badge>
                  </td>
                  <td className="num">
                    {befund ? (
                      befund.fehler ? (
                        <span style={{ color: 'var(--bad)' }}>Fehler</span>
                      ) : (
                        befund.faellig.toLocaleString('de-DE')
                      )
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="small">{befund?.aeltester ? formatDate(befund.aeltester) : '—'}</td>
                  <td className="num">
                    <button className="small ghost" onClick={() => umschalten(regel)} disabled={busy}>
                      {regel.aktiv ? 'aussetzen' : 'aktivieren'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {bericht && (
        <Notice kind={bericht.gesperrt ? 'info' : bericht.faelligGesamt > 0 ? 'info' : 'success'}>
          Lauf vom {formatDateTime(bericht.gelaufenAm)}:{' '}
          {bericht.faelligGesamt === 0
            ? 'Nichts hat seine Frist überschritten.'
            : `${bericht.faelligGesamt.toLocaleString('de-DE')} Sätze hätten ihre Frist überschritten.`}
          {bericht.gesperrt && ' Eine Aufbewahrungssperre läuft — es dürfte ohnehin nichts gelöscht werden.'}
        </Notice>
      )}

      <Card
        title="Aufbewahrungssperren"
        actions={
          <button
            className="small"
            onClick={() => setNeueSperre(neueSperre ? null : { grund: '', giltBis: '' })}
          >
            {neueSperre ? 'Abbrechen' : 'Sperre setzen'}
          </button>
        }
        padded={false}
      >
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Solange eine Betriebsprüfung läuft oder ein Rechtsstreit anhängig ist, darf nichts gelöscht
          werden — auch nicht, was die Frist eigentlich freigäbe. Eine Sperre wird nie gelöscht,
          sondern beendet; so bleibt nachvollziehbar, wann sie galt.
        </div>

        {neueSperre && (
          <div className="body">
            <TextField
              label="Grund"
              value={neueSperre.grund}
              onChange={(v) => setNeueSperre({ ...neueSperre, grund: v })}
              placeholder="z. B. Betriebsprüfung 2022–2024, Anordnung vom …"
            />
            <TextField
              label="Voraussichtlich bis (optional)"
              type="date"
              value={neueSperre.giltBis}
              onChange={(v) => setNeueSperre({ ...neueSperre, giltBis: v })}
            />
            <button className="primary small" onClick={sperreAnlegen} disabled={busy}>
              Sperre setzen
            </button>
          </div>
        )}

        {sperren.length === 0 ? (
          <Empty>Keine Sperre gesetzt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Grund</th>
                <th>Ab</th>
                <th>Bis</th>
                <th>Status</th>
                <th>Gesetzt von</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sperren.map((sperre) => (
                <tr key={sperre.id}>
                  <td>{sperre.grund}</td>
                  <td className="small">{formatDate(sperre.gilt_ab)}</td>
                  <td className="small">{sperre.gilt_bis ? formatDate(sperre.gilt_bis) : 'offen'}</td>
                  <td>
                    <Badge kind={sperre.laeuft ? 'warn' : 'default'}>
                      {sperre.laeuft && !sperre.beendbar
                        ? 'läuft, endet heute'
                        : sperre.laeuft
                          ? 'läuft'
                          : 'beendet'}
                    </Badge>
                  </td>
                  <td className="small">{sperre.angelegt_von_name ?? '—'}</td>
                  <td className="num">
                    {sperre.beendbar && (
                      <button className="small ghost" onClick={() => sperreBeenden(sperre)} disabled={busy}>
                        beenden
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Bisherige Läufe" padded={false}>
        {laeufe.length === 0 ? (
          <Empty>Noch kein Lauf. Der Zeitplan startet ihn monatlich von selbst.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>Art</th>
                <th className="num">Fällig gewesen</th>
                <th>Gesperrt</th>
                <th>Ausgelöst von</th>
              </tr>
            </thead>
            <tbody>
              {laeufe.map((lauf) => (
                <tr key={lauf.id}>
                  <td className="small muted">{formatDateTime(lauf.gelaufen_am)}</td>
                  <td>
                    <Badge kind="info">Trockenlauf</Badge>
                  </td>
                  <td className="num">
                    {lauf.ergebnis
                      .reduce((summe, befund) => summe + befund.faellig, 0)
                      .toLocaleString('de-DE')}
                  </td>
                  <td className="small">{lauf.gesperrt ? 'ja' : 'nein'}</td>
                  <td className="small">{lauf.ausgeloest_von_name ?? 'Zeitplan'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
